import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Control, StoredOfflineEditDefect } from '../model/control.model';
import { Store } from '@ngrx/store';
import { removeOfflineDefect, setFullScreenDefectPicture, updateOfflineDefect, uploadOfflineDefects } from '../store/control.action';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { selectOfflineDefects } from '../store/control.selector';
import { DialogService } from 'src/app/shared/service/dialog.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { EditDefectComponent } from '../edit-defect/edit-defect.component';
import { DarkButtonComponent } from '../../shared/buttons/dark-button.component';
import { SafeUrlPipe } from '../../shared/pipes/safe-url.pipe';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'sibat-defects-list-offline',
  template: `
    <div *transloco="let t; read: 'building.control'" class="gap-between-items">
      @for (defect of offlineDefects; track defect; let i = $index) {
      <div>
        <div
          class="defect-item"
          [class.missing-defect-info]="tooltipContents[i]"
          [matTooltip]="tooltipContents[i]"
          matTooltipPosition="left"
        >
          <div class="cloud-icon">
            <mat-icon>cloud_off</mat-icon>
          </div>
          <div class="defect-icon">
            @if (defect.picturesToAdd.length === 0) {
            <mat-icon>format_align_left</mat-icon>
            } @if (defect.picturesToAdd[0]?.preview; as preview) {
            <img [src]="preview | safeUrl" alt="" (click)="displayFullScreenImg(preview)" />
            }
          </div>
          <div class="defect-description">
            @if (defect.description) {
            <span>{{ defect.description }}</span>
            } @if (!defect.description) {
            <span>{{ t('noDescription') }}</span>
            } @if (defect.deadline) {
            <div class="defect-deadline">
              <mat-icon class="icon">update</mat-icon>
              <span class="date">{{ defect.deadline | date : 'dd.MM.yyyy' }} </span>
            </div>
            }
          </div>
          <button class="inline-button update-button" mat-flat-button (click)="toggleEditing(i)">
            <mat-icon>mode_edit</mat-icon>
          </button>
          <button class="inline-button delete-button" mat-flat-button (click)="openDialogRemoveConfirmation(defect.id)">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
        @if (isEditing[i]) {
        <sibat-edit-defect [defect]="defect" (defectSaved)="updateDefect($event, i)" class="zone-edit"> </sibat-edit-defect>
        }
      </div>
      } @if (!offline && offlineDefects.length > 0) {
      <sibat-dark-button
        (clicked)="uploadOfflineDefects()"
        [label]="t('uploadOfflineDefects')"
        icon="cloud_upload"
        data-testid="upload-defects"
      >
      </sibat-dark-button>
      }
    </div>
  `,
  styleUrls: ['defects-list-offline.component.scss'],
  imports: [TranslocoDirective, MatTooltip, MatIcon, MatButton, EditDefectComponent, DarkButtonComponent, SafeUrlPipe, DatePipe],
})
export class DefectsListOfflineComponent implements OnInit, OnDestroy {
  @Input() control: Control;
  @Input() buildingId: number | undefined;
  @Input() offline: boolean;
  isEditing: boolean[];
  minDate: Date;
  tooltipContents: string[];
  langSubscription: Subscription;
  offlineDefects: StoredOfflineEditDefect[] = [];
  offlineDefectsSubscription: Subscription;

  constructor(private store: Store, private dialogService: DialogService, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    const today = new Date();
    this.minDate = new Date(today.setDate(today.getDate() + 1));

    this.langSubscription = this.translocoService.langChanges$
      .pipe(switchMap(() => this.translocoService.selectTranslation()))
      .subscribe(() => this.updateTooltipContents());
    this.offlineDefectsSubscription = this.store.select(selectOfflineDefects).subscribe(defects => (this.offlineDefects = defects));
    this.isEditing = this.offlineDefects.map(() => false);
  }

  ngOnDestroy(): void {
    this.langSubscription?.unsubscribe();
    this.offlineDefectsSubscription?.unsubscribe();
  }

  updateTooltipContents() {
    this.tooltipContents = this.offlineDefects.map(defect => {
      let content = '';
      if (!defect.type) {
        content += this.translocoService.translate('building.control.missingDefectTypeTooltip') + '\n';
      }
      if (!defect.location) {
        content += this.translocoService.translate('building.control.missingDefectLocationTooltip');
      }
      return content;
    });
  }

  toggleEditing(indexInList: number) {
    this.isEditing[indexInList] = !this.isEditing[indexInList];
  }

  async openDialogRemoveConfirmation(defectOfflineId: number): Promise<void> {
    const result = await this.dialogService.requestConfirmation({ translationKey: 'building.control.sureToRemove' });
    if (result) {
      this.store.dispatch(removeOfflineDefect({ defectOfflineId }));
    }
  }

  updateDefect(defect: StoredOfflineEditDefect, defectIndex: number): void {
    this.store.dispatch(updateOfflineDefect({ defect }));
    this.isEditing[defectIndex] = false;
  }

  uploadOfflineDefects(): void {
    this.store.dispatch(uploadOfflineDefects({ controlId: this.control.id }));
  }

  displayFullScreenImg(pictureData: string) {
    this.store.dispatch(setFullScreenDefectPicture({ pictureData }));
  }
}
